import { Mail, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cvData } from '../../data/cv';
import { MagneticButton } from '../effects/MagneticButton';

const PORTRAIT_URL = '/lovable-uploads/yesh-portrait-2026.png';

export const Hero = () => {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const scrollToAbout = () => document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToProjects = () => document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="min-h-screen gradient-hero relative overflow-hidden texture-noise pt-20 pb-16 md:pt-24 md:pb-20">
      {/* Ambient blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-24 w-[420px] sm:w-[640px] h-[420px] sm:h-[640px] rounded-full bg-primary/12 blur-[120px] animate-blob" />
        <div className="absolute top-1/3 -left-32 w-[360px] sm:w-[560px] h-[360px] sm:h-[560px] rounded-full bg-accent/12 blur-[110px] animate-blob animation-delay-2000" />
      </div>

      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `linear-gradient(hsl(217 52% 24%) 1px, transparent 1px), linear-gradient(90deg, hsl(217 52% 24%) 1px, transparent 1px)`,
        backgroundSize: '80px 80px'
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-20 relative z-10">
        <div className={`grid md:grid-cols-10 gap-10 md:gap-12 lg:gap-16 items-center transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

          {/* Left 60% — content */}
          <div className="md:col-span-6 space-y-6 md:space-y-8">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-accent/30 bg-card/60 backdrop-blur-sm shadow-card">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-accent">Healthcare Professional</span>
            </div>

            <h1
              className="text-[2.5rem] leading-[1.02] sm:text-5xl sm:leading-[0.98] md:text-6xl lg:text-7xl xl:text-8xl md:leading-[0.92] font-black text-foreground tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Advancing <span className="italic font-normal text-primary">Clinical</span> Excellence Through <span className="text-accent">Tech.</span>
            </h1>

            <div className="flex flex-col md:flex-row gap-5 md:gap-8 items-start">
              <div className="w-1 bg-accent h-16 md:h-24 hidden md:block flex-shrink-0 mt-2" />
              <p className="text-base sm:text-lg md:text-xl text-foreground/75 leading-relaxed max-w-xl font-light">
                Clinician and healthcare innovator combining hands-on patient care with technology-driven solutions. Founder of <span className="font-medium text-primary">RotaCare.uk</span>, dedicated to transforming primary care.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2">
              <MagneticButton
                onClick={scrollToProjects}
                className="group px-7 sm:px-8 py-3.5 sm:py-4 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs hover:bg-primary/90 transition-all duration-300 shadow-hero hover:shadow-glow rounded-md flex items-center justify-center gap-2"
              >
                View Portfolio
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </MagneticButton>
              <MagneticButton
                strength={0.18}
                onClick={() => navigate('/contact')}
                className="group px-7 sm:px-8 py-3.5 sm:py-4 border border-primary/30 text-primary font-bold uppercase tracking-widest text-xs hover:bg-primary hover:text-primary-foreground transition-all duration-300 rounded-md flex items-center justify-center gap-2"
              >
                <Mail size={14} />
                Get in Touch
              </MagneticButton>
            </div>
          </div>

          {/* Right 40% — portrait */}
          <div className="md:col-span-4 relative mx-auto md:mx-0 w-full max-w-[280px] sm:max-w-xs md:max-w-none">
            <div className="aspect-[4/5] bg-card overflow-hidden rounded-2xl border-[8px] md:border-[12px] border-card shadow-elevated relative z-10">
              <img
                src={PORTRAIT_URL}
                alt={`Dr. ${cvData.personal.name}`}
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700 ease-out"
              />
            </div>
            {/* Gold offset frame */}
            <div className="absolute -bottom-3 -right-3 md:-bottom-6 md:-right-6 inset-0 border-2 border-accent rounded-2xl opacity-25 -z-0 pointer-events-none" />
            {/* Floating stat card — bottom-left on mobile to avoid covering face */}
            <div className="absolute -bottom-4 -left-3 md:top-1/2 md:bottom-auto md:-left-10 lg:-left-12 md:-translate-y-1/2 bg-card px-4 py-3 md:p-6 shadow-elevated rounded-lg z-20 border-l-4 border-accent">
              <p className="text-xl md:text-3xl font-bold text-foreground leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
                {cvData.experience.length}+
              </p>
              <p className="text-[9px] md:text-[10px] uppercase tracking-tight font-bold text-muted-foreground mt-1 leading-tight">
                Roles in<br />Medicine
              </p>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <button
          onClick={scrollToAbout}
          className={`mt-12 md:mt-16 lg:mt-20 flex flex-col items-center gap-3 text-muted-foreground hover:text-accent transition-all duration-300 mx-auto group ${visible ? 'opacity-100' : 'opacity-0'}`}
        >
          <span className="text-[10px] uppercase tracking-[0.3em] font-semibold">Scroll to explore</span>
          <div className="w-6 h-10 rounded-full border-2 border-current flex items-start justify-center p-1.5">
            <div className="w-1 h-2 bg-current rounded-full animate-bounce" />
          </div>
        </button>
      </div>
    </section>
  );
};
